import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  completeStep,
  decrementCurrentStep,
  incrementCurrentStep,
  selectConsumerPlan,
  selectQuote,
  selectRegistrationOrder,
  selectRegistrationSteps,
  validProgramDetailsStep,
  validProgramMaterialsStep,
} from '../../../reducers/programRegistrationReducer'
import { requestEnrollment } from '../../../actions/programRegistrationActions'
import { getSignUpPageProgram } from '../../../actions/programActions'
import { displayTotals, totalsByCurrency } from '../../../utility/currency'
import useCardPaymentFlow from '../../../hooks/useCardPaymentFlow'

import { Col, Container, Row } from 'reactstrap'
import ConfirmationModal2 from '../../common/ConfirmationModal2'
import SubmitButton from '../../common/buttons/SubmitButton'

import './ProgramRegistrationFooter.scss'

const ProgramRegistrationFooter = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const payByCard = useCardPaymentFlow()

  const course = useSelector(state => state.programPresentation.course)
  const {
    status,
    currentStep,
    completedSteps,
    selectedPaymentMethod,
    paymentSession,
    isProcessingPayment,
  } = useSelector(state => state.programRegistration)
  const order = useSelector(selectRegistrationOrder)
  const steps = useSelector(selectRegistrationSteps)
  const consumerPlan = useSelector(selectConsumerPlan)
  const quote = useSelector(selectQuote)
  const validFirstStep = useSelector(validProgramDetailsStep)
  const validSecondStep = useSelector(validProgramMaterialsStep)
  const usesPaymentSession = consumerPlan || selectedPaymentMethod === 'card'

  const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState(false)

  const isLoading = status === 'processing registration'
  const isCheckoutStep = steps[currentStep] === 'checkout'
  const isValidStep = useMemo(() => {
    switch (steps[currentStep]) {
      case 'programDetails':
        return validFirstStep
      case 'programMaterials':
        return validSecondStep
      case 'membership':
        return paymentSession?.status === 'ready' && quote?.status === 'ready'
      case 'checkout':
        return usesPaymentSession
          ? paymentSession?.status === 'ready' && quote?.status === 'ready'
          : !!selectedPaymentMethod
      default:
        return true
    }
  }, [currentStep, steps, validFirstStep, validSecondStep, selectedPaymentMethod, paymentSession, quote, usesPaymentSession])

  const handleGoBack = () => {
    if (currentStep === 0) {
      setShowCancelConfirmationModal(true)
    } else {
      dispatch(decrementCurrentStep())
    }
  }

  const registrationUrl = (registrationId) => `/landing/${course.community.id}/class/${course.id}/registration/${registrationId}`

  const goToCheckout = () => {
    usesPaymentSession
      ? payByCard()
        .then(registrationId => {
          if (registrationId) {
            navigate(registrationUrl(registrationId))
          }
        })
      : dispatch(requestEnrollment(order)).unwrap()
        .then(data => {
          navigate(registrationUrl(data.enrollment.registration.id))
        })
  }

  const handleGoNext = () => {
    if (!completedSteps.includes(currentStep)) {
      dispatch(completeStep(currentStep))
    }
    if (isCheckoutStep) {
      goToCheckout()
    } else {
      dispatch(incrementCurrentStep())
    }
  }

  const cardLines = quote?.pricing?.lines.filter(line => line.paymentMethod === 'card') || []
  const submitLabel = isCheckoutStep
    ? (quote?.cardRequired
      ? t('programs.registration.footer.pay', { amount: displayTotals(totalsByCurrency(cardLines)) })
      : t('programs.registration.footer.confirmRegistration'))
    : t('programs.registration.footer.nextStep')

  const navigateToClassPage = () => {
    dispatch(getSignUpPageProgram(course.id))
    navigate(`/landing/${course.community.id}/class/${course.id}`)
  }

  return (
    <Container id='ProgramRegistrationFooter' fluid>
      <Row className='h-100 flex-column-reverse flex-sm-row justify-content-center align-items-center'>
        <Col sm={6} className='go-back'>
          <SubmitButton
            noTopMargin
            onClick={handleGoBack}
            disabled={isLoading || isProcessingPayment}
            buttonMessage={t('programs.registration.footer.goBack')}
            buttonColor='navy-button'
          />
        </Col>
        <Col sm={6} className='submit-register-button'>
          <SubmitButton
            noTopMargin
            disabled={!isValidStep || isLoading}
            loading={isProcessingPayment}
            onClick={handleGoNext}
            buttonMessage={submitLabel}
          />
        </Col>

        <ConfirmationModal2
          showModal={showCancelConfirmationModal}
          closeModal={() => setShowCancelConfirmationModal(false)}
          onDeny={() => setShowCancelConfirmationModal(false)}
          denyMessage={t('common.no')}
          onProceed={navigateToClassPage}
          proceedMessage={t('common.yes')}
          icon={<i className='fa fa-question-circle' style={{ fontSize: '72px' }} />}
          title={t('programs.registration.footer.cancelTitle')}
          message={t('programs.registration.footer.cancelMessage')}
        />
      </Row>
    </Container>
  )
}

ProgramRegistrationFooter.propTypes = {
  fetchCourse: PropTypes.func,
}

export default ProgramRegistrationFooter
